import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ImagePlus, Monitor, Moon, RotateCcw, Smartphone, Sun, Trash2 } from 'lucide-react';
import {
  accentReport,
  BRAND_ORIGIN_TEXT,
  BRAND_PALETTE,
  EMPTY_BRANDING,
  normalizeHex,
  type BrandingEditState,
  type BrandingValues,
  type BrandLogo,
  type ResolvedBranding,
} from '@kaba/shared';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../lib/toast';
import { useThemeMode } from '../../lib/theme';
import { Button, cx, Sheet } from '../ui/ui';
import { BrandPreview } from './BrandPreview';

export type BrandTargetUi = { scope: 'instance' } | { scope: 'team'; teamId: string; name: string } | { scope: 'board'; boardId: string; name: string };

const endpoint = (t: BrandTargetUi) => (t.scope === 'instance' ? '/api/branding/instance' : t.scope === 'team' ? `/api/teams/${t.teamId}/branding` : `/api/boards/${t.boardId}/branding`);
const keyOf = (t: BrandTargetUi) => ['branding', t.scope, t.scope === 'instance' ? '' : t.scope === 'team' ? t.teamId : t.boardId];
const SCOPE_HINT: Record<BrandTargetUi['scope'], string> = {
  instance: 'Gilt für Anmeldung, Übersicht und alle Teams und Boards, die diesen Wert übernehmen.',
  team: 'Gilt für alle Boards des Teams, die diesen Wert übernehmen. Boards mit eigener Gestaltung bleiben unverändert.',
  board: 'Gilt für alle, die dieses Board sehen – auch für Gäste. Hell/Dunkel wählt jede Person selbst.',
};
const PARENT_NAME: Record<BrandTargetUi['scope'], string> = { instance: 'KaBa-Standard', team: 'Installation', board: 'Team bzw. Installation' };

type AssetMap = BrandingEditState['assets'];

/** Wirksamer Stand aus eigenen Werten und dem Elternstand – nur für die Vorschau */
function effective(
  values: BrandingValues,
  parent: ResolvedBranding,
  assets: AssetMap,
): { logo: BrandLogo | null; accent: string | null; header: 'neutral' | 'tinted'; background: ResolvedBranding['background'] } {
  let logo = parent.logo;
  if (values.logo.mode === 'none') logo = null;
  if (values.logo.mode === 'custom') {
    const m = assets[values.logo.main];
    const d = values.logo.dark ? assets[values.logo.dark] : undefined;
    const k = values.logo.mark ? assets[values.logo.mark] : undefined;
    logo = m ? { main: m.url, dark: d?.url ?? null, mark: k?.url ?? null, ratio: m.width / m.height, markRatio: k ? k.width / k.height : null } : null;
  }
  return {
    logo,
    accent: values.accent.mode === 'custom' ? (normalizeHex(values.accent.hex) ?? parent.accent) : parent.accent,
    header: values.header === 'inherit' ? parent.header : values.header,
    background: bgOf(values.background, parent, assets),
  };
}

function bgOf(b: BrandingValues['background'], parent: ResolvedBranding, assets: AssetMap): ResolvedBranding['background'] {
  if (b.mode === 'inherit') return parent.background;
  if (b.mode === 'image') {
    const a = assets[b.image];
    // Vorschau mit der kleinen Variante; die Auflösung wählt im Board der Server bzw. die Bildschirmbreite
    return a ? { ...b, urls: { w960: a.url } } : { mode: 'solid', color: b.color };
  }
  return b;
}

/** Erscheinungsbild einer Ebene bearbeiten: Identität, Farbe, Kopfzeile, Vorschau (U01–U03) */
export function BrandingDialog({ target, open, onClose }: { target: BrandTargetUi; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const { data, refetch, error } = useQuery({ queryKey: keyOf(target), queryFn: () => api.get<BrandingEditState>(endpoint(target)), enabled: open });
  const [draft, setDraft] = useState<BrandingValues | null>(null);
  const [assets, setAssets] = useState<AssetMap>({});
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState<number | null>(null);
  const loadedVersion = useRef<number | null>(null);
  // Beim Öffnen immer aus den (ggf. zwischengespeicherten) Daten befüllen – auch wenn sich `data` seit
  // dem letzten Öffnen nicht geändert hat (sonst bliebe nach Speichern + erneutem Öffnen „Lade …“ stehen)
  useEffect(() => {
    if (!open) {
      loadedVersion.current = null;
      setDraft(null);
      setConflict(null);
      return;
    }
    if (data && loadedVersion.current !== data.version) {
      loadedVersion.current = data.version;
      setDraft(data.values);
      setAssets(data.assets);
    }
  }, [data, open]);
  const dirty = !!data && !!draft && JSON.stringify(draft) !== JSON.stringify(data.values);

  const close = () => {
    if (dirty && !window.confirm('Ungespeicherte Änderungen am Erscheinungsbild verwerfen?')) return;
    onClose();
  };

  const save = async (version: number) => {
    if (!draft) return;
    if (draft.background.mode === 'image' && !draft.background.image) {
      toast.error('Bitte zuerst ein Hintergrundbild hochladen – oder eine andere Art wählen.');
      return;
    }
    if (draft.logo.mode === 'custom' && !draft.logo.main) {
      toast.error('Bitte zuerst ein Hauptlogo hochladen – oder „Standard übernehmen“ bzw. „Kein Logo“ wählen.');
      return;
    }
    setBusy(true);
    try {
      const r = await api.put<BrandingEditState>(endpoint(target), { values: draft, version });
      loadedVersion.current = r.version;
      qc.setQueryData(keyOf(target), r);
      setDraft(r.values);
      setConflict(null);
      void qc.invalidateQueries({ queryKey: ['boards'] });
      if (target.scope === 'instance') void qc.invalidateQueries({ queryKey: ['config'] });
      toast.info('Erscheinungsbild gespeichert.');
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        const d = (e.data as { details?: { version?: number } } | undefined)?.details;
        setConflict(d?.version ?? 0);
      } else toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const title = target.scope === 'instance' ? 'Erscheinungsbild der Installation' : target.scope === 'team' ? `Erscheinungsbild – ${target.name}` : `Erscheinungsbild – ${target.name}`;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && close()} title={title} side="wide" onEscapeKeyDown={(e) => dirty && (e.preventDefault(), close())}>
      {error ? (
        <p className="p-4 text-sm" role="alert">
          {(error as Error).message}
        </p>
      ) : !data || !draft ? (
        <p className="p-4 text-sm text-[var(--ink-soft)]">Lade …</p>
      ) : (
        <div className="flex min-h-full flex-col">
          <div className="grid flex-1 grid-cols-1 gap-4 p-3 sm:p-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
            <div className="flex min-w-0 flex-col gap-4">
              <p className="text-sm text-[var(--ink-soft)]">{SCOPE_HINT[target.scope]}</p>
              {!data.canEdit && (
                <p className="rounded-lg bg-[var(--column)] p-3 text-sm" role="status">
                  {target.scope === 'board' ? 'Nur Boardbesitzer ändern das gemeinsame Erscheinungsbild.' : 'Nur Teamadministratoren und -besitzer ändern das Erscheinungsbild.'}
                </p>
              )}
              <fieldset disabled={!data.canEdit} className="flex min-w-0 flex-col gap-4">
                <LogoSection target={target} draft={draft} setDraft={setDraft} assets={assets} setAssets={setAssets} parent={data.parent} />
                <ColorSection draft={draft} setDraft={setDraft} parent={data.parent} scope={target.scope} />
                <Section title="Kopfzeile" origin={draft.header === 'inherit' ? `${BRAND_ORIGIN_TEXT[data.parent.headerOrigin]}: ${data.parent.header === 'tinted' ? 'dezent in Markenfarbe' : 'neutral'}` : undefined}>
                  <Choice
                    name="header"
                    value={draft.header}
                    onChange={(v) => setDraft({ ...draft, header: v as BrandingValues['header'] })}
                    options={[
                      { value: 'inherit', label: `Standard übernehmen (${PARENT_NAME[target.scope]})` },
                      { value: 'neutral', label: 'Neutral' },
                      { value: 'tinted', label: 'Dezent in Markenfarbe' },
                    ]}
                  />
                </Section>
                <BackgroundSection target={target} draft={draft} setDraft={setDraft} assets={assets} setAssets={setAssets} parent={data.parent} />
              </fieldset>
            </div>
            <PreviewPane eff={effective(draft, data.parent, assets)} name={target.scope === 'instance' ? 'Installation' : target.name} />
          </div>
          <div className="pb-safe sticky bottom-0 z-10 flex flex-wrap items-center justify-end gap-2 border-t border-[var(--line)] bg-[var(--panel)] px-3 py-2 sm:px-4">
            {conflict !== null ? (
              <div role="alert" className="flex flex-1 flex-wrap items-center gap-2 text-sm">
                <span className="flex-1">Inzwischen hat jemand anderes das Erscheinungsbild geändert.</span>
                <Button
                  onClick={() => {
                    loadedVersion.current = null;
                    setConflict(null);
                    void refetch();
                  }}
                >
                  Neu laden
                </Button>
                <Button variant="primary" disabled={busy} onClick={() => void save(conflict)}>
                  Meine Änderungen übernehmen
                </Button>
              </div>
            ) : (
              <>
                <span className="basis-full text-xs text-[var(--ink-soft)] sm:basis-auto sm:flex-1" aria-live="polite">
                  {dirty ? 'Ungespeicherte Änderungen – sichtbar erst nach dem Speichern.' : ''}
                </span>
                {data.canEdit && target.scope !== 'instance' && JSON.stringify(draft) !== JSON.stringify(EMPTY_BRANDING) && (
                  <Button variant="ghost" onClick={() => setDraft(EMPTY_BRANDING)}>
                    <RotateCcw size={15} aria-hidden /> <span className="sm:hidden">Zurücksetzen</span>
                    <span className="max-sm:hidden">Auf Standard zurücksetzen</span>
                  </Button>
                )}
                <Button onClick={close}>Abbrechen</Button>
                {data.canEdit && (
                  <Button variant="primary" disabled={!dirty || busy} onClick={() => void save(data.version)}>
                    Änderungen speichern
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </Sheet>
  );
}


function Section({ title, origin, children }: { title: string; origin?: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-2.5 rounded-xl border border-[var(--line)] p-3 sm:p-4" aria-label={title}>
      <div>
        <h3 className="text-sm font-semibold">{title}</h3>
        {origin && <p className="text-xs text-[var(--ink-soft)]">{origin}</p>}
      </div>
      {children}
    </section>
  );
}

function Choice({ name, value, options, onChange }: { name: string; value: string; options: { value: string; label: string }[]; onChange: (v: string) => void }) {
  return (
    <div role="radiogroup" className="flex flex-col gap-1">
      {options.map((o) => (
        <label key={o.value} className="flex min-h-11 items-center gap-2.5 rounded-lg px-1 text-sm sm:min-h-9">
          <input type="radio" name={name} className="size-5 accent-[var(--color-accent)]" checked={value === o.value} onChange={() => onChange(o.value)} />
          {o.label}
        </label>
      ))}
    </div>
  );
}

function LogoSection({
  target,
  draft,
  setDraft,
  assets,
  setAssets,
  parent,
}: {
  target: BrandTargetUi;
  draft: BrandingValues;
  setDraft: React.Dispatch<React.SetStateAction<BrandingValues | null>> & ((v: BrandingValues) => void);
  assets: AssetMap;
  setAssets: (fn: (a: AssetMap) => AssetMap) => void;
  parent: ResolvedBranding;
}) {
  const logo = draft.logo;
  const [uploading, setUploading] = useState<string | null>(null);
  const upload = async (slot: 'main' | 'dark' | 'mark', file: File) => {
    setUploading(slot);
    try {
      const fd = new FormData();
      fd.set('file', file);
      fd.set('kind', 'logo');
      fd.set('scope', target.scope);
      fd.set('scopeId', target.scope === 'instance' ? '' : target.scope === 'team' ? target.teamId : target.boardId);
      const a = await api.post<{ id: string; url: string; width: number; height: number }>('/api/branding/assets', fd);
      setAssets((m) => ({ ...m, [a.id]: { url: a.url, width: a.width, height: a.height, kind: 'logo' } }));
      // Funktional aktualisieren: mehrere Uploads hintereinander dürfen sich nicht überschreiben
      setDraft((d) => {
        if (!d) return d;
        const cur = d.logo.mode === 'custom' ? d.logo : { mode: 'custom' as const, main: '', dark: null, mark: null };
        return { ...d, logo: { ...cur, [slot]: a.id } as BrandingValues['logo'] };
      });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setUploading(null);
    }
  };
  const slot = (key: 'main' | 'dark' | 'mark', label: string, hint: string, dark?: boolean) => {
    const id = logo.mode === 'custom' ? logo[key] : null;
    const a = id ? assets[id] : undefined;
    return (
      <div className="flex flex-wrap items-center gap-3">
        <div
          className={cx('flex h-16 w-32 shrink-0 items-center justify-center rounded-lg border border-[var(--line)] p-2', dark ? 'bg-[#1f2228]' : 'bg-white')}
          aria-hidden={!a}
        >
          {a ? <img src={a.url} alt={`${label} (Vorschau)`} className="max-h-full max-w-full object-contain" /> : <span className={cx('text-xs', dark ? 'text-slate-400' : 'text-slate-500')}>{key === 'main' ? 'Noch kein Logo' : 'Optional'}</span>}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-sm font-medium">{label}</span>
          <span className="text-xs text-[var(--ink-soft)]">{hint}</span>
          <div className="flex flex-wrap gap-2">
            <label className={cx('inline-flex min-h-10 cursor-pointer items-center gap-1.5 rounded-lg border border-[var(--line)] px-3 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/5', uploading && 'opacity-60')}>
              <ImagePlus size={15} aria-hidden />
              {uploading === key ? 'Lädt hoch …' : a ? 'Ersetzen' : 'Hochladen'}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="sr-only"
                aria-label={`${label} ${a ? 'ersetzen' : 'hochladen'}`}
                disabled={!!uploading}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) void upload(key, f);
                }}
              />
            </label>
            {a && key !== 'main' && logo.mode === 'custom' && (
              <Button size="sm" variant="ghost" onClick={() => setDraft({ ...draft, logo: { ...logo, [key]: null } })} aria-label={`${label} entfernen`}>
                <Trash2 size={14} aria-hidden /> Entfernen
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };
  return (
    <Section
      title="Logo"
      origin={logo.mode === 'inherit' ? `${BRAND_ORIGIN_TEXT[parent.logoOrigin]}: ${parent.logo ? 'Logo' : 'kein Logo'}` : logo.mode === 'none' ? 'Kein Logo – auch kein übergeordnetes.' : undefined}
    >
      <Choice
        name="logo"
        value={logo.mode}
        onChange={(v) => {
          if (v === 'custom') setDraft({ ...draft, logo: { mode: 'custom', main: logo.mode === 'custom' ? logo.main : '', dark: null, mark: null } });
          else setDraft({ ...draft, logo: { mode: v as 'inherit' | 'none' } });
        }}
        options={[
          { value: 'inherit', label: `Standard übernehmen (${PARENT_NAME[target.scope]})` },
          { value: 'custom', label: target.scope === 'board' ? 'Eigenes Logo (ersetzt das übernommene)' : 'Eigenes Logo' },
          { value: 'none', label: 'Kein Logo' },
        ]}
      />
      {logo.mode === 'custom' && (
        <div className="flex flex-col gap-3 border-t border-[var(--line)] pt-3">
          {slot('main', 'Hauptlogo', 'PNG, JPEG oder WebP, max. 5 MB. Transparenz bleibt erhalten, nichts wird beschnitten.')}
          {logo.main && slot('dark', 'Für den dunklen Modus', 'Optional. Fehlt sie, steht das Hauptlogo im Dunkeln auf einer hellen Fläche.', true)}
          {logo.main && slot('mark', 'Signet', 'Optional, möglichst quadratisch – für schmale Bildschirme.')}
          {!logo.main && <p className="text-xs text-amber-800 dark:text-amber-200">Lade ein Hauptlogo hoch, um zu speichern.</p>}
        </div>
      )}
    </Section>
  );
}

function ColorSection({ draft, setDraft, parent, scope }: { draft: BrandingValues; setDraft: (v: BrandingValues) => void; parent: ResolvedBranding; scope: BrandTargetUi['scope'] }) {
  const a = draft.accent;
  const [text, setText] = useState(a.mode === 'custom' ? a.hex : '');
  useEffect(() => {
    if (a.mode === 'custom') setText(a.hex);
  }, [a]);
  const valid = normalizeHex(text);
  const report = a.mode === 'custom' ? accentReport(a.hex) : null;
  const set = (hex: string) => setDraft({ ...draft, accent: { mode: 'custom', hex } });
  return (
    <Section title="Markenfarbe" origin={a.mode === 'inherit' ? `${BRAND_ORIGIN_TEXT[parent.accentOrigin]}: ${parent.accent ?? 'KaBa-Blau'}` : undefined}>
      <Choice
        name="accent"
        value={a.mode}
        onChange={(v) => (v === 'custom' ? set(parent.accent ?? '#3b5bdb') : setDraft({ ...draft, accent: { mode: 'inherit' } }))}
        options={[
          { value: 'inherit', label: `Standard übernehmen (${PARENT_NAME[scope]})` },
          { value: 'custom', label: 'Eigene Farbe' },
        ]}
      />
      {a.mode === 'custom' && (
        <div className="flex flex-col gap-3 border-t border-[var(--line)] pt-3">
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Vorschläge">
            {BRAND_PALETTE.map((p) => (
              <button
                key={p.hex}
                type="button"
                className={cx('relative size-11 rounded-full border-2 sm:size-9', a.hex === p.hex ? 'border-[var(--ink)]' : 'border-transparent')}
                style={{ background: p.hex }}
                aria-label={p.name}
                aria-pressed={a.hex === p.hex}
                onClick={() => set(p.hex)}
              >
                {a.hex === p.hex && <Check size={16} className="absolute inset-0 m-auto text-white mix-blend-difference" aria-hidden />}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1 text-sm font-medium">
              Farbe wählen
              <input type="color" className="h-11 w-16 cursor-pointer rounded-lg border border-[var(--line)] bg-[var(--panel)] p-1" value={a.hex} onChange={(e) => set(e.target.value)} />
            </label>
            <label className="flex flex-col gap-1 text-sm font-medium">
              HEX-Wert
              <input
                className="min-h-11 w-32 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-3 font-mono text-base sm:min-h-9 sm:text-sm"
                value={text}
                aria-invalid={!valid || undefined}
                aria-describedby="hex-hint"
                onChange={(e) => {
                  setText(e.target.value);
                  const n = normalizeHex(e.target.value);
                  if (n) set(n);
                }}
              />
            </label>
          </div>
          <p id="hex-hint" className={cx('text-xs', valid ? 'text-[var(--ink-soft)]' : 'text-rose-700 dark:text-rose-300')} role={valid ? undefined : 'alert'}>
            {valid ? 'z. B. #0b7285' : 'Bitte als HEX-Wert eingeben, z. B. #0b7285.'}
          </p>
          {report?.some((r) => r.adjusted || r.darkText) && (
            <p className="rounded-lg bg-[var(--column)] p-2.5 text-xs">
              Für Schrift, Links und Buttons nutzt KaBa abgeleitete Töne, damit alles gut lesbar bleibt
              {report.some((r) => r.darkText) ? ' – auf Buttons mit dunkler Schrift' : ''}. Die Originalfarbe bleibt deine Markenfarbe.
            </p>
          )}
        </div>
      )}
    </Section>
  );
}

function PreviewPane({ eff, name }: { eff: ReturnType<typeof effective>; name: string }) {
  const sys = useThemeMode();
  const [mode, setMode] = useState<'light' | 'dark'>(sys);
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const toggle = useMemo(
    () =>
      ({ value, set, options }: { value: string; set: (v: never) => void; options: { v: string; label: string; icon: React.ReactNode }[] }) => (
        <div className="inline-flex rounded-lg bg-[var(--column)] p-0.5" role="group">
          {options.map((o) => (
            <button
              key={o.v}
              type="button"
              aria-pressed={value === o.v}
              onClick={() => set(o.v as never)}
              className={cx('inline-flex min-h-10 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium sm:min-h-8', value === o.v ? 'bg-[var(--panel)] shadow-sm' : 'text-[var(--ink-soft)]')}
            >
              {o.icon}
              {o.label}
            </button>
          ))}
        </div>
      ),
    [],
  );
  return (
    <div className="flex min-w-0 flex-col gap-2 lg:sticky lg:top-0 lg:self-start">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="mr-auto text-sm font-semibold">Vorschau</h3>
        {toggle({
          value: device,
          set: setDevice as (v: never) => void,
          options: [
            { v: 'desktop', label: 'Desktop', icon: <Monitor size={14} aria-hidden /> },
            { v: 'mobile', label: 'Mobil', icon: <Smartphone size={14} aria-hidden /> },
          ],
        })}
        {toggle({
          value: mode,
          set: setMode as (v: never) => void,
          options: [
            { v: 'light', label: 'Hell', icon: <Sun size={14} aria-hidden /> },
            { v: 'dark', label: 'Dunkel', icon: <Moon size={14} aria-hidden /> },
          ],
        })}
      </div>
      <BrandPreview logo={eff.logo} accent={eff.accent} header={eff.header} background={eff.background} mode={mode} device={device} name={name} />
      <p className="text-xs text-[var(--ink-soft)]">Beispielinhalte – keine echte Karte wird verändert.</p>
    </div>
  );
}

const BG_PRESETS = ['#e7f5ff', '#e6fcf5', '#fff9db', '#fff0f6', '#f3f0ff', '#dee2e6', '#0b7285', '#1d2128'];
const GRADIENTS: [string, string][] = [
  ['#e7f5ff', '#f3f0ff'],
  ['#e6fcf5', '#e7f5ff'],
  ['#fff9db', '#ffe8cc'],
  ['#0b7285', '#364fc7'],
];
const FOCUS: { x: number; y: number; label: string }[] = [
  { x: 0, y: 0, label: 'oben links' },
  { x: 50, y: 0, label: 'oben Mitte' },
  { x: 100, y: 0, label: 'oben rechts' },
  { x: 0, y: 50, label: 'Mitte links' },
  { x: 50, y: 50, label: 'Mitte' },
  { x: 100, y: 50, label: 'Mitte rechts' },
  { x: 0, y: 100, label: 'unten links' },
  { x: 50, y: 100, label: 'unten Mitte' },
  { x: 100, y: 100, label: 'unten rechts' },
];
const BG_TEXT: Record<string, string> = { neutral: 'neutral', solid: 'Farbe', gradient: 'Verlauf', image: 'Bild' };

/** Boardhintergrund: erst die Art, dann nur die passenden Optionen (U01, V05) */
function BackgroundSection({
  target,
  draft,
  setDraft,
  assets,
  setAssets,
  parent,
}: {
  target: BrandTargetUi;
  draft: BrandingValues;
  setDraft: React.Dispatch<React.SetStateAction<BrandingValues | null>> & ((v: BrandingValues) => void);
  assets: AssetMap;
  setAssets: (fn: (a: AssetMap) => AssetMap) => void;
  parent: ResolvedBranding;
}) {
  const bg = draft.background;
  const [uploading, setUploading] = useState(false);
  const set = (b: BrandingValues['background']) => setDraft({ ...draft, background: b });
  const upload = async (file: File) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.set('file', file);
      fd.set('kind', 'background');
      fd.set('scope', target.scope);
      fd.set('scopeId', target.scope === 'instance' ? '' : target.scope === 'team' ? target.teamId : target.boardId);
      const a = await api.post<{ id: string; url: string; width: number; height: number }>('/api/branding/assets', fd);
      setAssets((m) => ({ ...m, [a.id]: { url: a.url, width: a.width, height: a.height, kind: 'background' } }));
      setDraft((d) =>
        d ? { ...d, background: { mode: 'image', image: a.id, color: d.background.mode === 'image' ? d.background.color : '#dee2e6', focusX: 50, focusY: 50, dim: 'balanced' } } : d,
      );
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setUploading(false);
    }
  };
  const colorInput = (label: string, value: string, onValue: (v: string) => void) => (
    <label className="flex items-center gap-2 text-sm font-medium">
      <input type="color" className="h-11 w-14 cursor-pointer rounded-lg border border-[var(--line)] bg-[var(--panel)] p-1" value={value} onChange={(e) => onValue(e.target.value)} />
      {label}
    </label>
  );
  const img = bg.mode === 'image' ? assets[bg.image] : undefined;
  return (
    <Section
      title="Boardhintergrund"
      origin={bg.mode === 'inherit' ? `${BRAND_ORIGIN_TEXT[parent.backgroundOrigin]}: ${BG_TEXT[parent.background.mode] ?? 'neutral'}` : 'Hinter den Spalten in Board- und Listenansicht. Karten, Tabelle, Kalender und Zeitleiste bleiben ruhig.'}
    >
      <Choice
        name="background"
        value={bg.mode}
        onChange={(v) => {
          if (v === 'inherit' || v === 'neutral') set({ mode: v });
          else if (v === 'solid') set({ mode: 'solid', color: BG_PRESETS[0]! });
          else if (v === 'gradient') set({ mode: 'gradient', from: GRADIENTS[0]![0], to: GRADIENTS[0]![1] });
          else if (bg.mode !== 'image') {
            const existing = Object.entries(assets).find(([, a]) => a.kind === 'background');
            if (existing) set({ mode: 'image', image: existing[0], color: '#dee2e6', focusX: 50, focusY: 50, dim: 'balanced' });
            else setDraft({ ...draft, background: { mode: 'image', image: '', color: '#dee2e6', focusX: 50, focusY: 50, dim: 'balanced' } });
          }
        }}
        options={[
          { value: 'inherit', label: `Standard übernehmen (${PARENT_NAME[target.scope]})` },
          { value: 'neutral', label: 'Neutral' },
          { value: 'solid', label: 'Einfarbig' },
          { value: 'gradient', label: 'Ruhiger Verlauf' },
          { value: 'image', label: 'Eigenes Bild' },
        ]}
      />
      {bg.mode === 'solid' && (
        <div className="flex flex-col gap-3 border-t border-[var(--line)] pt-3">
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Hintergrundfarben">
            {BG_PRESETS.map((c) => (
              <button key={c} type="button" aria-label={`Farbe ${c}`} aria-pressed={bg.color === c} onClick={() => set({ mode: 'solid', color: c })} className={cx('size-11 rounded-lg border-2 sm:size-9', bg.color === c ? 'border-[var(--ink)]' : 'border-[var(--line)]')} style={{ background: c }} />
            ))}
          </div>
          {colorInput('Eigene Farbe', bg.color, (v) => set({ mode: 'solid', color: v }))}
        </div>
      )}
      {bg.mode === 'gradient' && (
        <div className="flex flex-col gap-3 border-t border-[var(--line)] pt-3">
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Verläufe">
            {GRADIENTS.map(([a, b]) => (
              <button
                key={a + b}
                type="button"
                aria-label={`Verlauf ${a} bis ${b}`}
                aria-pressed={bg.from === a && bg.to === b}
                onClick={() => set({ mode: 'gradient', from: a, to: b })}
                className={cx('h-11 w-16 rounded-lg border-2 sm:h-9', bg.from === a && bg.to === b ? 'border-[var(--ink)]' : 'border-[var(--line)]')}
                style={{ background: `linear-gradient(135deg, ${a}, ${b})` }}
              />
            ))}
          </div>
          <div className="flex flex-wrap gap-4">
            {colorInput('Von', bg.from, (v) => set({ ...bg, from: v }))}
            {colorInput('Bis', bg.to, (v) => set({ ...bg, to: v }))}
          </div>
        </div>
      )}
      {bg.mode === 'image' && (
        <div className="flex flex-col gap-3 border-t border-[var(--line)] pt-3">
          <div className="flex flex-wrap items-center gap-2">
            <label className={cx('inline-flex min-h-10 cursor-pointer items-center gap-1.5 rounded-lg border border-[var(--line)] px-3 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/5', uploading && 'opacity-60')}>
              <ImagePlus size={15} aria-hidden />
              {uploading ? 'Lädt hoch …' : img ? 'Bild ersetzen' : 'Bild hochladen'}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="sr-only"
                aria-label={img ? 'Hintergrundbild ersetzen' : 'Hintergrundbild hochladen'}
                disabled={uploading}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) void upload(f);
                }}
              />
            </label>
            <span className="text-xs text-[var(--ink-soft)]">PNG, JPEG oder WebP, max. 10 MB und 25 Megapixel.</span>
          </div>
          {img && (
            <>
              <fieldset className="flex flex-col gap-1.5">
                <legend className="mb-1 text-sm font-medium">Wichtigster Bildbereich</legend>
                <div className="flex flex-wrap items-start gap-3">
                  <div className="relative aspect-[16/10] w-48 overflow-hidden rounded-lg border border-[var(--line)]" style={{ backgroundImage: `url("${img.url}")`, backgroundSize: 'cover', backgroundPosition: `${bg.focusX}% ${bg.focusY}%` }}>
                    <div className="absolute inset-0 grid grid-cols-3 grid-rows-3">
                      {FOCUS.map((f) => (
                        <button
                          key={f.label}
                          type="button"
                          aria-label={`Bildbereich ${f.label}`}
                          aria-pressed={bg.focusX === f.x && bg.focusY === f.y}
                          onClick={() => set({ ...bg, focusX: f.x, focusY: f.y })}
                          className="flex items-center justify-center border border-white/30 hover:bg-white/20"
                        >
                          {bg.focusX === f.x && bg.focusY === f.y && <span className="size-3 rounded-full bg-white ring-2 ring-black/60" aria-hidden />}
                        </button>
                      ))}
                    </div>
                  </div>
                  <p className="max-w-56 text-xs text-[var(--ink-soft)]">Dieser Bereich bleibt sichtbar, wenn das Bild auf schmalen Bildschirmen beschnitten wird. Die Vorschau „Mobil“ zeigt den Ausschnitt.</p>
                </div>
              </fieldset>
              <div>
                <span className="mb-1 block text-sm font-medium" id="dim-label">
                  Abschwächung
                </span>
                <Choice
                  name="dim"
                  value={bg.dim}
                  onChange={(v) => set({ ...bg, dim: v as 'calm' | 'balanced' | 'strong' })}
                  options={[
                    { value: 'calm', label: 'Ruhig – Bild nur angedeutet' },
                    { value: 'balanced', label: 'Ausgewogen' },
                    { value: 'strong', label: 'Deutlich' },
                  ]}
                />
              </div>
              {colorInput('Farbe, falls das Bild nicht lädt oder ausgeblendet ist', bg.color, (v) => set({ ...bg, color: v }))}
            </>
          )}
          {!img && <p className="text-xs text-amber-800 dark:text-amber-200">Lade ein Bild hoch, um zu speichern.</p>}
        </div>
      )}
    </Section>
  );
}
